import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Card, CardContent, Dialog, DialogTitle, DialogContent, TextField, DialogActions, MenuItem, Select, FormControl, InputLabel, LinearProgress } from '@mui/material';
import { okrApi } from '../services/okrApi';
import { TeamObjective, OrgObjective } from '../types';
import httpClient from '@utils/httpClient';
import { useAuth } from '@store/authStore';
import OKRCard from './OKRCard'; // Reuse the shared premium card

const OKRTeamView: React.FC = () => {
    const { user } = useAuth();
    const [teamObjectives, setTeamObjectives] = useState<TeamObjective[]>([]);
    const [orgObjectives, setOrgObjectives] = useState<OrgObjective[]>([]);
    const [openCreate, setOpenCreate] = useState(false);
    const [loading, setLoading] = useState(true);

    // Filters
    const [departments, setDepartments] = useState<any[]>([]);
    const [selectedDept, setSelectedDept] = useState<number | undefined>(undefined);

    // Form
    const [title, setTitle] = useState('');
    const [selectedOrgObj, setSelectedOrgObj] = useState('');
    const [weight, setWeight] = useState(1);

    const canCreateTeamObj = user?.role === 'Manager' || user?.is_manager === 1 || user?.is_manager === true;
    const canFilterDept = user?.role === 'HR';

    const fetchData = async () => {
        try {
            setLoading(true);
            const [data, orgData] = await Promise.all([
                okrApi.getTeamOkrs(selectedDept),
                okrApi.getOrgOkrs()
            ]);
            setTeamObjectives(data);
            setOrgObjectives(orgData);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const fetchDepartments = async () => {
        try {
            const response = await httpClient.get<{ success: boolean; data: any[] }>('/hr/employees/departments/list');
            if (response.data) setDepartments(response.data);
        } catch (error) { console.error(error); }
    };

    useEffect(() => {
        if (canFilterDept) fetchDepartments();
    }, [canFilterDept]);

    useEffect(() => {
        fetchData();
    }, [selectedDept]);

    const handleCreate = async () => {
        if (!title || !selectedOrgObj) return;
        try {
            await okrApi.createTeamObjective(selectedOrgObj, Number(user?.department_id) || 1, title, '', weight);
            setOpenCreate(false);
            setTitle('');
            setSelectedOrgObj('');
            setWeight(1);
            fetchData();
        } catch (error) { console.error(error); }
    };

    if (loading) return <LinearProgress />;

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', py: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: '#1A2027', mb: 0.5 }}>
                        Team Objectives
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Departmental goals that align with the organization.
                    </Typography>
                </Box>
                {canCreateTeamObj && (
                    <Button
                        variant="contained"
                        onClick={() => setOpenCreate(true)}
                        sx={{
                            borderRadius: '10px',
                            background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
                        }}
                    >
                        + New Team Goal
                    </Button>
                )}
            </Box>

            {canFilterDept && (
                <Box mb={4}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Filter by Department</InputLabel>
                        <Select
                            value={selectedDept || ''}
                            label="Filter by Department"
                            onChange={(e) => setSelectedDept(e.target.value ? Number(e.target.value) : undefined)}
                        >
                            <MenuItem value=""><em>All Departments</em></MenuItem>
                            {departments.map((dept: any) => (
                                <MenuItem key={dept.id} value={dept.id}>{dept.dept_name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            )}

            <Box>
                {teamObjectives.map((obj) => (
                    <OKRCard
                        key={obj.id}
                        title={obj.title}
                        description={`Aligns with: ${obj.org_title || 'Organization'}`}
                        score={obj.score}
                        type="TEAM"
                    // Add team-specific logic here if needed
                    >
                        {/* Could show team members or sub-tasks here */}
                    </OKRCard>
                ))}
                {teamObjectives.length === 0 && (
                    <Box sx={{ p: 6, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.6)', borderRadius: 4, border: '1px dashed #ccc' }}>
                        <Typography color="text.secondary">No team objectives found.</Typography>
                    </Box>
                )}
            </Box>

            <Dialog open={openCreate} onClose={() => setOpenCreate(false)} fullWidth maxWidth="sm">
                <DialogTitle>New Team Objective</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2, mb: 3 }}>
                        <InputLabel>Org Objective Alignment</InputLabel>
                        <Select value={selectedOrgObj} label="Org Objective Alignment" onChange={(e) => setSelectedOrgObj(e.target.value)}>
                            {orgObjectives.map((o) => (
                                <MenuItem key={o.id} value={o.id}>{o.title}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField label="Title" fullWidth value={title} onChange={(e) => setTitle(e.target.value)} sx={{ mb: 3 }} />
                    <TextField label="Weight (1-5)" type="number" fullWidth value={weight} onChange={(e) => setWeight(Number(e.target.value))} />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreate}>Create</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OKRTeamView;
